import { api } from '../api-client';
import { openTransactionListSheet } from '../components/transaction-list-sheet';

function getSheetMount(): HTMLElement {
  let mount = document.getElementById('tx-list-sheet-mount');
  if (!mount) {
    mount = document.createElement('div');
    mount.id = 'tx-list-sheet-mount';
    document.body.appendChild(mount);
  }
  return mount;
}

interface SubcategoryReport {
  category_id: string;
  name: string;
  total_aed: number;
}

interface ParentCategoryReport {
  category_id: string;
  name: string;
  total_aed: number;
  pct_of_spending: number;
  parent_direct_aed: number;
  subcategories: SubcategoryReport[];
}

interface ReportsMonthPriorBlock {
  month: string;
  income_aed: number;
  spending_aed: number;
  net_aed: number;
  available: boolean;
}

interface ReportsMonthResponse {
  month: string;
  bounds: { start: string; end: string };
  income_aed: number;
  spending_aed: number;
  net_aed: number;
  prior_month: ReportsMonthPriorBlock;
  by_category: ParentCategoryReport[];
}

interface ReportsState {
  month: string;
  loading: boolean;
  data: ReportsMonthResponse | null;
  error: string | null;
  expanded: Set<string>;
}

function currentDubaiMonth(): string {
  const ms = Date.now() + 4 * 60 * 60 * 1000;
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function readMonthFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const m = params.get('month');
  if (m && /^\d{4}-(0[1-9]|1[0-2])$/.test(m)) return m;
  return currentDubaiMonth();
}

function formatAed(n: number): string {
  const sign = n < 0 ? '−' : '';
  return `${sign}AED ${Math.abs(n).toLocaleString('en-AE', { maximumFractionDigits: 0 })}`;
}

function monthLabel(month: string): string {
  const [yStr, mStr] = month.split('-');
  const date = new Date(Date.UTC(Number(yStr), Number(mStr) - 1, 1));
  return date.toLocaleString('en-AE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function priorMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  let yy = y, mm = m - 1;
  if (mm === 0) { mm = 12; yy -= 1; }
  return `${yy}-${String(mm).padStart(2, '0')}`;
}

function nextMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  let yy = y, mm = m + 1;
  if (mm === 13) { mm = 1; yy += 1; }
  return `${yy}-${String(mm).padStart(2, '0')}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function skeletonHtml(): string {
  const headlineGhost = `
    <div class="sk sk-label"></div>
    <div class="sk sk-value"></div>
    <div class="sk sk-delta"></div>
  `;
  const catGhost = `<div class="sk sk-cat-row"></div>`;
  return `
    <div class="reports-skeleton">
      ${headlineGhost.repeat(3)}
      ${catGhost.repeat(4)}
    </div>
  `;
}

function emptyStateHtml(month: string): string {
  return `<p class="reports-empty">No transactions logged in ${escapeHtml(monthLabel(month))}.</p>`;
}

function deltaChip(delta: number | null, kind: 'income' | 'spending' | 'net'): string {
  if (delta === null || delta === 0) return '';
  const isGood = kind === 'spending' ? delta < 0 : delta > 0;
  const arrow = delta > 0 ? '▴' : '▾';
  const sign = delta > 0 ? '+' : '−';
  const cls = isGood ? 'reports-delta-good' : 'reports-delta-bad';
  return `<span class="reports-delta ${cls}">${arrow} ${sign}${formatAed(Math.abs(delta))} vs prior month</span>`;
}

function headlineHtml(d: ReportsMonthResponse): string {
  const p = d.prior_month;
  const incomeDelta = p.available ? d.income_aed - p.income_aed : null;
  const spendDelta  = p.available ? d.spending_aed - p.spending_aed : null;
  const netDelta    = p.available ? d.net_aed - p.net_aed : null;
  return `
    <section class="reports-headline">
      <div class="reports-row">
        <span class="reports-label">Income</span>
        <span class="reports-value">${formatAed(d.income_aed)}</span>
        ${deltaChip(incomeDelta, 'income')}
      </div>
      <div class="reports-row">
        <span class="reports-label">Spending</span>
        <span class="reports-value">${formatAed(d.spending_aed)}</span>
        ${deltaChip(spendDelta, 'spending')}
      </div>
      <div class="reports-row">
        <span class="reports-label">Net</span>
        <span class="reports-value">${formatAed(d.net_aed)}</span>
        ${deltaChip(netDelta, 'net')}
      </div>
    </section>
  `;
}

function subListHtml(p: ParentCategoryReport): string {
  const subs = p.subcategories.map((s) => `
    <div class="reports-sub-row" data-drill-sub="${escapeHtml(s.category_id)}">
      <span class="reports-sub-name">${escapeHtml(s.name)}</span>
      <span class="reports-sub-total">${formatAed(s.total_aed)}</span>
    </div>
  `).join('');
  const directRow = p.parent_direct_aed > 0
    ? `<div class="reports-sub-row reports-sub-direct" data-drill="${escapeHtml(p.category_id)}">
         <span class="reports-sub-name">(not subcategorised)</span>
         <span class="reports-sub-total">${formatAed(p.parent_direct_aed)}</span>
       </div>`
    : '';
  return `<div class="reports-sub-list">${subs}${directRow}</div>`;
}

function parentRowHtml(p: ParentCategoryReport, isOpen: boolean): string {
  const pct = Math.round(p.pct_of_spending * 100);
  const hasSubs = p.subcategories.length > 0;
  const chev = hasSubs ? (isOpen ? '⌄' : '›') : '';
  const action = hasSubs
    ? `data-toggle="${escapeHtml(p.category_id)}"`
    : `data-drill="${escapeHtml(p.category_id)}"`;
  return `
    <div class="reports-cat" data-cat-id="${escapeHtml(p.category_id)}">
      <button class="reports-cat-row" ${action}>
        <span class="reports-cat-name">${escapeHtml(p.name)}</span>
        <span class="reports-cat-total">${formatAed(p.total_aed)}</span>
        <span class="reports-cat-pct">${pct}% of spending</span>
        ${hasSubs ? `<span class="reports-cat-chev">${chev}</span>` : ''}
      </button>
      ${hasSubs && isOpen ? subListHtml(p) : ''}
    </div>
  `;
}

function byCategoryHtml(d: ReportsMonthResponse, expanded: Set<string>): string {
  if (d.by_category.length === 0) return '';
  return `
    <section class="reports-cats">
      <h2 class="reports-cats-title">By category</h2>
      ${d.by_category.map((p) => parentRowHtml(p, expanded.has(p.category_id))).join('')}
    </section>
  `;
}

function renderInto(root: HTMLElement, state: ReportsState): void {
  root.innerHTML = `
    <div class="reports">
      <header class="reports-header">
        <div class="reports-back-row">
          <a class="reports-back" href="/review">← Family review</a>
        </div>
        <div class="reports-nav-row">
          <button class="reports-chev" id="rep-prev" aria-label="Previous month">‹</button>
          <h1 class="reports-title">${escapeHtml(monthLabel(state.month))}</h1>
          <button class="reports-chev" id="rep-next" aria-label="Next month">›</button>
        </div>
      </header>
      <div class="reports-body" id="rep-body">${state.loading ? skeletonHtml() : ''}</div>
    </div>
  `;

  const body = root.querySelector<HTMLElement>('#rep-body')!;
  if (state.error) {
    body.innerHTML = `
      <div class="reports-error">
        <p>${escapeHtml(state.error)}</p>
        <button class="ghost" id="rep-retry" type="button">Retry</button>
      </div>
    `;
    body.querySelector<HTMLButtonElement>('#rep-retry')!.addEventListener('click', () => {
      state.loading = true;
      state.error = null;
      renderInto(root, state);
      void load(state, root);
    });
  } else if (state.data) {
    const d = state.data;
    const hasData = d.income_aed > 0 || d.spending_aed > 0 || d.by_category.length > 0;
    if (!hasData) {
      body.innerHTML = emptyStateHtml(state.month);
    } else {
      body.innerHTML = headlineHtml(d) + byCategoryHtml(d, state.expanded);
      body.querySelectorAll<HTMLButtonElement>('[data-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-toggle')!;
          if (state.expanded.has(id)) state.expanded.delete(id);
          else state.expanded.add(id);
          renderInto(root, state);
        });
      });
      body.querySelectorAll<HTMLElement>('[data-drill]').forEach((el) => {
        el.addEventListener('click', () => {
          const leafId = el.getAttribute('data-drill')!;
          openTransactionListSheet(getSheetMount(), {
            month: state.month,
            leafId,
            onClose: () => {
              state.loading = true;
              state.data = null;
              state.expanded.clear();
              renderInto(root, state);
              void load(state, root);
            },
          });
        });
      });
      body.querySelectorAll<HTMLElement>('[data-drill-sub]').forEach((el) => {
        el.addEventListener('click', () => {
          const leafId = el.getAttribute('data-drill-sub')!;
          openTransactionListSheet(getSheetMount(), {
            month: state.month,
            leafId,
            onClose: () => {
              state.loading = true;
              state.data = null;
              state.expanded.clear();
              renderInto(root, state);
              void load(state, root);
            },
          });
        });
      });
    }
  }

  const next = root.querySelector<HTMLButtonElement>('#rep-next')!;
  if (state.month >= currentDubaiMonth()) {
    // Can't navigate to the future — hide the dead control but keep its slot so the title stays centred.
    next.disabled = true;
    next.style.visibility = 'hidden';
  }

  root.querySelector<HTMLButtonElement>('#rep-prev')!.addEventListener('click', () => {
    state.month = priorMonth(state.month);
    state.loading = true;
    state.data = null;
    state.expanded.clear();
    if (typeof history !== 'undefined') history.replaceState(null, '', `/reports?month=${state.month}`);
    renderInto(root, state);
    void load(state, root);
  });
  next.addEventListener('click', () => {
    if (next.disabled) return;
    state.month = nextMonth(state.month);
    state.loading = true;
    state.data = null;
    state.expanded.clear();
    if (typeof history !== 'undefined') history.replaceState(null, '', `/reports?month=${state.month}`);
    renderInto(root, state);
    void load(state, root);
  });
}

async function load(state: ReportsState, root: HTMLElement): Promise<void> {
  try {
    const data = await api<ReportsMonthResponse>(`/api/reports/month?month=${state.month}`);
    state.data = data;
    state.error = null;
  } catch (e) {
    state.data = null;
    state.error = (e as Error).message;
  }
  state.loading = false;
  renderInto(root, state);
}

export async function renderReports(root: HTMLElement): Promise<void> {
  const state: ReportsState = {
    month: readMonthFromUrl(),
    loading: true,
    data: null,
    error: null,
    expanded: new Set(),
  };
  renderInto(root, state);
  await load(state, root);
}
